<template>
  <div v-if="show" class="modal-overlay" @click.self="closeModal">
    <div class="modal-container">
      <!-- Header avec progression -->
      <div class="modal-header">
        <div class="header-content">
          <div class="header-info">
            <h2>{{ isRecouvrement ? '💰 Recouvrement' : '🔧 Intervention Complète' }}</h2>
            <p>{{ isRecouvrement ? 'Paiement + Finalisation en 2 étapes' : 'Affectation produit + Finalisation en 4 étapes' }}</p>
          </div>
          <button @click="closeModal" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <!-- Stepper Navigation -->
        <div class="stepper-container">
          <div class="stepper">
            <div 
              v-for="(step, index) in steps" 
              :key="index"
              class="step-item"
              :class="{ 
                'active': currentStep === index + 1,
                'completed': currentStep > index + 1
              }"
            >
              <div class="step-circle">
                <i v-if="currentStep > index + 1" class="fas fa-check"></i>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <div class="step-content">
                <div class="step-title">{{ step.title }}</div>
                <div class="step-subtitle">{{ step.subtitle }}</div>
              </div>
              <div v-if="index < steps.length - 1" class="step-connector"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Barre de prix RDV - visible dans tous les steps -->
      <div class="prix-rdv-bar">
        <div class="prix-rdv-content">
          <div class="prix-rdv-label">
            <i class="fas fa-receipt"></i>
            <span>Prix RDV</span>
          </div>
          <div class="prix-rdv-value">
            {{ formatPrice(prixIntervention) }} TND
          </div>
        </div>
      </div>

      <!-- Corps du modal avec étapes -->
      <div class="modal-body">
        <!-- Étape 1: Affectation Produit (masqué pour recouvrement) -->
        <div v-if="realStep === 1 && !isRecouvrement" class="step-content-wrapper">
          <div class="step-header">
            <i class="fas fa-box step-icon"></i>
            <div>
              <h3>📦 Affectation Produit</h3>
              <p>Sélectionnez un produit depuis votre sac technicien</p>
            </div>
          </div>
          
          <!-- Recherche produit -->
          <div class="search-section">
            <div class="search-input-wrapper">
              <i class="fas fa-search"></i>
              <input 
                v-model="searchQuery"
                type="text" 
                placeholder="Rechercher dans votre sac..."
                class="search-input"
              >
            </div>
          </div>

          <!-- Liste des produits -->
          <div class="products-list">
            <div v-if="filteredProducts.length === 0" class="no-products-message">
              <i class="fas fa-info-circle"></i>
              <p>Aucun produit dans votre sac</p>
              <small>Récupérez des produits au magasin avant l'intervention</small>
            </div>
            
            <div 
              v-for="product in filteredProducts" 
              :key="product._id"
              class="product-card"
              :class="{ 'selected': isProductSelected(product._id) }"
              @click="toggleProductSelection(product)"
            >
              <div class="product-info">
                <div class="product-header">
                  <span class="product-ref">{{ getProductIdentifier(product) }}</span>
                  <span class="product-category">{{ product.categorie }}</span>
                </div>
                <div class="product-name">{{ product.designation }}</div>
                <div class="product-details">
                  <span class="product-brand">{{ product.marque }} {{ product.modele }}</span>
                </div>
              </div>
              <div class="selection-indicator">
                <i class="fas fa-check"></i>
              </div>
            </div>
          </div>

          <!-- Produits sélectionnés -->
          <div v-if="selectedProducts.length > 0" class="selected-products-section">
            <label>📦 Produits sélectionnés ({{ selectedProducts.length }}) :</label>
            <div class="selected-products-list">
              <div v-for="product in selectedProducts" :key="product._id" class="selected-product-item">
                <div class="selected-product-info">
                  <span class="selected-product-name">{{ product.designation }}</span>
                  <span class="selected-product-ref">{{ getProductIdentifier(product) }}</span>
                </div>
                <button @click="removeSelectedProduct(product._id)" class="btn-remove-product">
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
            <div class="selected-products-total">
              <strong>Prix RDV :</strong> {{ formatPrice(prixIntervention) }} TND
            </div>
          </div>
        </div>

        <!-- Étape 2: Photo Appareil (masqué pour recouvrement) -->
        <div v-if="realStep === 2 && !isRecouvrement" class="step-content-wrapper">
          <div class="step-header">
            <i class="fas fa-camera step-icon"></i>
            <div>
              <h3>📷 Photo de l'Appareil</h3>
              <p>Prenez une photo de l'appareil installé</p>
            </div>
          </div>

          <div class="photo-appareil-form">
            <!-- Zone d'upload photo -->
            <div class="upload-zone-appareil" @click="triggerAppareilPhotoUpload">
              <input 
                ref="appareilPhotoInput"
                type="file" 
                accept="image/*"
                capture="environment"
                multiple
                @change="handleAppareilPhotoUpload"
                style="display: none"
              >
              <div class="upload-content">
                <i class="fas fa-camera fa-3x"></i>
                <p>Cliquez pour prendre une photo ou sélectionner une image</p>
                <small>Formats acceptés: JPG, PNG, HEIC</small>
              </div>
            </div>

            <!-- Aperçu des photos -->
            <div v-if="appareilPhotos.length > 0" class="appareil-photos-preview">
              <label>📸 Photos de l'appareil ({{ appareilPhotos.length }}) :</label>
              <div class="photos-grid">
                <div v-for="(photo, index) in appareilPhotos" :key="index" class="photo-item">
                  <img :src="photo" alt="Photo appareil" class="photo-preview">
                  <button @click="removeAppareilPhoto(index)" class="remove-photo-btn">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>
            </div>

            <div v-else class="no-photo-message">
              <i class="fas fa-info-circle"></i>
              <p>Aucune photo ajoutée</p>
              <small>Ajoutez au moins une photo de l'appareil installé</small>
            </div>
          </div>
        </div>

        <!-- Étape 3: Gestion Paiement (étape 1 pour recouvrement) -->
        <div v-if="realStep === 3" class="step-content-wrapper">
          <div class="step-header">
            <i class="fas fa-credit-card step-icon"></i>
            <div>
              <h3>💳 Gestion Paiement</h3>
              <p>Collectez l'encaissement et les justificatifs</p>
            </div>
          </div>

          <div class="payment-form">
            <!-- Code Log - Obligatoire uniquement pour Installation si pas renseigné avant -->
            <div class="form-group code-log-section" v-if="isCodeLogRequired">
              <label class="required">
                <i class="fas fa-barcode"></i>
                Login *
              </label>
              <input 
                v-model="paymentData.codeLog"
                type="text"
                placeholder="Ex: LOG-12345"
                class="form-control"
                required
              >
              <small class="form-hint">
                <i class="fas fa-info-circle"></i>
                Le login est obligatoire pour finaliser l'installation
              </small>
            </div>
            
            <!-- Afficher le code log existant (pour Installation) -->
            <div class="form-group code-log-display" v-else-if="isInstallation && hasExistingCodeLog">
              <label>
                <i class="fas fa-barcode"></i>
                Login
              </label>
              <div class="code-log-value">
                <i class="fas fa-check-circle text-success"></i>
                {{ existingCodeLog }}
              </div>
            </div>
            
            <!-- Champ Login optionnel pour Intervention (non obligatoire) -->
            <div class="form-group code-log-section" v-else-if="!isInstallation && !isRecouvrement && !hasExistingCodeLog">
              <label>
                <i class="fas fa-barcode"></i>
                Login (optionnel)
              </label>
              <input 
                v-model="paymentData.codeLog"
                type="text"
                placeholder="Ex: LOG-12345"
                class="form-control"
              >
            </div>

            <div class="payment-toggle">
              <label class="toggle-label">
                <input 
                  v-model="paymentData.paiementCollecte"
                  type="checkbox"
                  class="toggle-input"
                >
                <span class="toggle-slider"></span>
                Paiement collecté
              </label>
            </div>

            <div v-if="paymentData.paiementCollecte" class="payment-details">
              <!-- Section Tranches de paiement - Design Professionnel -->
              <div class="tranches-container">
                <!-- Header avec titre et bouton -->
                <div class="tranches-title-bar">
                  <div class="tranches-title">
                    <i class="fas fa-layer-group"></i>
                    <span>Modes de paiement</span>
                  </div>
                  <button type="button" @click="addTranche" class="btn-add-tranche">
                    <i class="fas fa-plus-circle"></i>
                    <span>Ajouter</span>
                  </button>
                </div>
                
                <!-- Liste des tranches -->
                <div class="tranches-list">
                  <div 
                    v-for="(tranche, index) in paymentData.tranches" 
                    :key="index" 
                    class="tranche-card"
                    :class="{ 'tranche-filled': tranche.montant > 0 && tranche.modePaiement }"
                  >
                    <!-- Badge numéro -->
                    <div class="tranche-badge">{{ index + 1 }}</div>
                    
                    <!-- Contenu de la tranche -->
                    <div class="tranche-fields">
                      <div class="tranche-field montant-field">
                        <label><i class="fas fa-coins"></i> Montant</label>
                        <div class="input-wrapper">
                          <input 
                            v-model.number="tranche.montant"
                            type="number" 
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            class="tranche-input"
                            @input="updateTotalMontant"
                          >
                          <span class="input-suffix">TND</span>
                        </div>
                      </div>
                      
                      <div class="tranche-field mode-field">
                        <label><i class="fas fa-credit-card"></i> Mode</label>
                        <div class="mode-buttons">
                          <button 
                            type="button"
                            v-for="mode in [
                              { value: 'Espèces', icon: 'fa-money-bill-wave', label: 'Espèces', color: '#28a745' },
                              { value: 'Chèque', icon: 'fa-money-check', label: 'Chèque', color: '#17a2b8' },
                              { value: 'CB', icon: 'fa-credit-card', label: 'CB', color: '#6f42c1' },
                              { value: 'Virement', icon: 'fa-university', label: 'Virement', color: '#fd7e14' }
                            ]"
                            :key="mode.value"
                            @click="tranche.modePaiement = mode.value"
                            class="mode-btn"
                            :class="{ 'active': tranche.modePaiement === mode.value }"
                            :style="tranche.modePaiement === mode.value ? `--mode-color: ${mode.color}` : ''"
                          >
                            <i :class="['fas', mode.icon]"></i>
                            <span>{{ mode.label }}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Bouton supprimer -->
                    <button 
                      v-if="paymentData.tranches.length > 1" 
                      type="button" 
                      @click="removeTranche(index)" 
                      class="btn-delete-tranche"
                      title="Supprimer cette tranche"
                    >
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
                
                <!-- Résumé des paiements - Design carte -->
                <div class="payment-summary-card">
                  <div class="summary-header">
                    <i class="fas fa-calculator"></i>
                    <span>Récapitulatif</span>
                  </div>
                  <div class="summary-body">
                    <div class="summary-line">
                      <span class="summary-label">Prix RDV</span>
                      <span class="summary-value prix-rdv">{{ formatPrice(prixIntervention) }}</span>
                    </div>
                    <div class="summary-line total-line">
                      <span class="summary-label">Total collecté</span>
                      <span 
                        class="summary-value total-value"
                        :class="{ 
                          'status-ok': totalTranches === prixIntervention,
                          'status-warning': totalTranches !== prixIntervention && totalTranches > 0
                        }"
                      >{{ formatPrice(totalTranches) }}</span>
                    </div>
                    <div v-if="totalTranches !== prixIntervention && totalTranches > 0" class="summary-line diff-line">
                      <span class="summary-label">Différence</span>
                      <span 
                        class="summary-value diff-value"
                        :class="totalTranches > prixIntervention ? 'diff-positive' : 'diff-negative'"
                      >
                        {{ totalTranches > prixIntervention ? '+' : '' }}{{ formatPrice(totalTranches - prixIntervention) }}
                      </span>
                    </div>
                  </div>
                  <!-- Indicateur de statut -->
                  <div 
                    v-if="totalTranches > 0"
                    class="summary-status"
                    :class="{ 
                      'status-success': totalTranches === prixIntervention,
                      'status-warning': totalTranches !== prixIntervention
                    }"
                  >
                    <i :class="totalTranches === prixIntervention ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'"></i>
                    <span>{{ totalTranches === prixIntervention ? 'Montant correct' : 'Justification requise' }}</span>
                  </div>
                </div>
              </div>

              <!-- Justification si montant différent du Prix RDV -->
              <div v-if="totalTranches && totalTranches !== prixIntervention" class="form-group justification-section">
                <label class="justification-label">
                  <i class="fas fa-exclamation-triangle"></i>
                  Justification de la différence de montant *
                </label>
                <textarea 
                  v-model="paymentData.justificationMontant"
                  class="justification-textarea"
                  rows="3"
                  :placeholder="totalTranches > prixIntervention 
                    ? 'Expliquez pourquoi le montant est supérieur au Prix RDV (ex: frais supplémentaires, pièces additionnelles...)' 
                    : 'Expliquez pourquoi le montant est inférieur au Prix RDV (ex: remise accordée, paiement partiel...)'"
                  required
                ></textarea>
                <small class="justification-hint">
                  Cette justification est obligatoire car le montant total ({{ formatPrice(totalTranches) }} TND) 
                  est {{ totalTranches > prixIntervention ? 'supérieur' : 'inférieur' }} 
                  au Prix RDV ({{ formatPrice(prixIntervention) }} TND)
                </small>
              </div>

              <!-- Upload justificatifs -->
              <div class="form-group">
                <label>Justificatifs de paiement</label>
                <div class="upload-zone" @click="triggerFileUpload">
                  <input 
                    ref="fileInput"
                    type="file" 
                    accept="image/*"
                    multiple
                    @change="handleFileUpload"
                    style="display: none"
                  >
                  <i class="fas fa-cloud-upload-alt"></i>
                  <p>Cliquez pour ajouter des photos</p>
                </div>
                <div v-if="paymentData.justificatifs.length > 0" class="uploaded-files">
                  <div v-for="(file, index) in paymentData.justificatifs" :key="index" class="file-item">
                    <img :src="file" alt="Justificatif" class="file-preview">
                    <button @click="removeFile(index)" class="remove-file">×</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Étape 4: Finalisation (étape 2 pour recouvrement) -->
        <div v-if="realStep === 4" class="step-content-wrapper">
          <div class="step-header">
            <i class="fas fa-check-circle step-icon"></i>
            <div>
              <h3>✅ Finalisation</h3>
              <p>Terminer l'Operation</p>
            </div>
          </div>

          <div class="finalization-form">
            <!-- Résumé final -->
            <div class="summary-section">
              <h4>📋 Résumé {{ isRecouvrement ? 'du recouvrement' : 'de l\'intervention' }}</h4>
              
              <!-- Produits (masqué pour recouvrement) -->
              <template v-if="!isRecouvrement">
                <div class="summary-item">
                  <strong>Produits ({{ selectedProducts.length }}) :</strong>
                </div>
                <div v-for="product in selectedProducts" :key="product._id" class="summary-product-item">
                  <span>• {{ product.designation }}</span>
                  <span v-if="product.imei || product.iccid" class="summary-imei">({{ product.imei || product.iccid }})</span>
                </div>
              </template>
              
              <div class="summary-item summary-total">
                <strong>{{ isRecouvrement ? 'Montant à recouvrer:' : 'Total produits:' }}</strong> {{ formatPrice(prixIntervention) }} TND
              </div>
              <div class="summary-item" v-if="paymentData.paiementCollecte">
                <strong>Paiement collecté:</strong> {{ formatPrice(totalTranches) }} TND
              </div>
              <div class="summary-item" v-if="!isRecouvrement && appareilPhotos.length > 0">
                <strong>Photos appareil:</strong> {{ appareilPhotos.length }} photo(s)
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer avec navigation -->
      <div class="modal-footer">
        <div class="footer-actions">
          <button 
            v-if="currentStep > 1"
            @click="previousStep" 
            class="btn-secondary"
          >
            <i class="fas fa-arrow-left"></i> Précédent
          </button>
          
          <button 
            v-if="currentStep < totalSteps"
            @click="nextStep" 
            :disabled="!canProceedToNextStep"
            class="btn-primary"
          >
            Suivant <i class="fas fa-arrow-right"></i>
          </button>
          
          <button 
            v-if="currentStep === totalSteps"
            @click="completeIntervention" 
            :disabled="isProcessing"
            class="btn-success"
          >
            <i class="fas fa-check"></i> 
            {{ isProcessing ? 'Traitement...' : 'Terminer l\'operation' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useToast } from 'vue-toastification'
import { useStore } from 'vuex'
import jsPDF from 'jspdf'
import QRCode from 'qrcode'
import { RECU_BASE_URL } from '@/config/api'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Capacitor } from '@capacitor/core'

// Fonction utilitaire pour télécharger PDF sur mobile
async function downloadPDFMobile(doc, fileName) {
  try {
    const isNative = Capacitor.isNativePlatform()
    if (isNative) {
      const base64Data = doc.output('datauristring').split(',')[1]
      const result = await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Documents,
        recursive: true
      })
      return { success: true, message: `Reçu téléchargé dans Documents/${fileName}`, path: result.uri }
    } else {
      doc.save(fileName)
      return { success: true, message: 'Reçu téléchargé avec succès' }
    }
  } catch (error) {
    console.error('Erreur téléchargement PDF:', error)
    try {
      const pdfBlob = doc.output('blob')
      const blobUrl = URL.createObjectURL(pdfBlob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000)
      return { success: true, message: 'Reçu téléchargé' }
    } catch (e) {
      return { success: false, message: 'Erreur lors du téléchargement' }
    }
  }
}

export default {
  name: 'CompletInterventionModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    intervention: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'intervention-completed', 'refresh-bag'],
  setup(props, { emit }) {
    const toast = useToast()
    const store = useStore()
    
    // État du modal
    const currentStep = ref(1)
    const isProcessing = ref(false)
    
    // Données des étapes
    const selectedProduct = ref(null) // Pour compatibilité
    const selectedProducts = ref([]) // Liste des produits sélectionnés
    const quantity = ref(1)
    const searchQuery = ref('')
    const products = ref([])
    
    const materialData = ref({
      imeiIccid: '',
      numeroSerie: ''
    })
    
    const paymentData = ref({
      paiementCollecte: false,
      montant: 0,
      modePaiement: '',
      justificatifs: [],
      justificationMontant: '',  // Justification si montant différent du Prix RDV
      tranches: [{ montant: 0, modePaiement: '' }],  // Système de tranches de paiement
      codeLog: ''  // Code Log obligatoire si pas renseigné avant
    })
    
    const finalizationData = ref({
      commentaires: ''
    })
    
    // Vérifier si le client a déjà un code log
    const existingCodeLog = computed(() => {
      return props.intervention?.prospect?.client?.codeLog || props.intervention?.codeLog || ''
    })
    
    const hasExistingCodeLog = computed(() => {
      return !!existingCodeLog.value
    })
    
    // Détecter si c'est un recouvrement
    const isRecouvrement = computed(() => {
      return props.intervention?.prospect?.typeIntervention === 'Recouvrement'
    })
    
    // Détecter si c'est une installation (Login obligatoire uniquement pour Installation)
    const isInstallation = computed(() => {
      return props.intervention?.prospect?.typeIntervention === 'Installation'
    })
    
    // Le code log est requis uniquement pour les installations
    const isCodeLogRequired = computed(() => {
      return isInstallation.value && !hasExistingCodeLog.value
    })
    
    // Configuration des étapes (différent pour recouvrement)
    const steps = computed(() => {
      if (isRecouvrement.value) {
        return [
          { title: 'Paiement', subtitle: 'Encaissement' },
          { title: 'Finalisation', subtitle: 'Terminé' }
        ]
      }
      return [
        { title: 'Affectation', subtitle: 'Sélection produit' },
        { title: 'Photo Appareil', subtitle: 'Image installation' },
        { title: 'Paiement', subtitle: 'Encaissement' },
        { title: 'Finalisation', subtitle: 'Terminé' }
      ]
    })
    
    // Nombre total d'étapes
    const totalSteps = computed(() => isRecouvrement.value ? 2 : 4)
    
    // Mapper l'étape actuelle vers l'étape réelle (pour recouvrement: 1->3, 2->4)
    const realStep = computed(() => {
      if (isRecouvrement.value) {
        return currentStep.value === 1 ? 3 : 4
      }
      return currentStep.value
    })
    
    // Données pour la photo de l'appareil
    const appareilPhotos = ref([])
    
    // Produits filtrés et triés par ordre croissant (par désignation)
    const filteredProducts = computed(() => {
      let result = products.value
      
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        result = result.filter(product => 
          product.designation.toLowerCase().includes(query) ||
          product.referenceInterne.toLowerCase().includes(query) ||
          (product.imei && product.imei.toLowerCase().includes(query)) ||
          (product.iccid && product.iccid.toLowerCase().includes(query))
        )
      }
      
      // Tri par ordre croissant (par désignation)
      return [...result].sort((a, b) => {
        const nameA = (a.designation || '').toLowerCase()
        const nameB = (b.designation || '').toLowerCase()
        return nameA.localeCompare(nameB, 'fr')
      })
    })
    
    // Validation des étapes (4 étapes normales, 2 étapes pour recouvrement)
    const canProceedToNextStep = computed(() => {
      // Pour recouvrement, utiliser realStep pour la validation
      const stepToValidate = isRecouvrement.value ? realStep.value : currentStep.value
      
      switch (stepToValidate) {
        case 1:
          return selectedProducts.value.length > 0
        case 2:
          // Photo appareil - au moins une photo requise
          return appareilPhotos.value.length > 0
        case 3:
          // Vérifier le code log uniquement si c'est une Installation et pas déjà renseigné
          if (isCodeLogRequired.value && !paymentData.value.codeLog?.trim()) {
            return false
          }
          // Si paiement collecté
          if (paymentData.value.paiementCollecte) {
            // Vérifier que toutes les tranches ont un montant et un mode de paiement
            const tranchesValides = paymentData.value.tranches.every(
              tranche => tranche.montant > 0 && tranche.modePaiement
            )
            if (!tranchesValides) {
              return false
            }
            // Si montant différent du Prix RDV, exiger une justification
            if (totalTranches.value !== prixIntervention.value && 
                !paymentData.value.justificationMontant?.trim()) {
              return false
            }
          }
          return true
        case 4:
          return true
        default:
          return false
      }
    })
    
    // Calculer le prix total des produits sélectionnés
    const totalSelectedPrice = computed(() => {
      return selectedProducts.value.reduce((total, product) => total + (product.prixVente || 0), 0)
    })
    
    // Prix de l'intervention depuis le RDV/Prospect
    const prixIntervention = computed(() => {
      return props.intervention?.prospect?.prix || props.intervention?.prix || 0
    })
    
    // Total des tranches de paiement
    const totalTranches = computed(() => {
      return paymentData.value.tranches.reduce((sum, tranche) => sum + (parseFloat(tranche.montant) || 0), 0)
    })
    
    // Ajouter une tranche de paiement
    const addTranche = () => {
      paymentData.value.tranches.push({ montant: 0, modePaiement: '' })
    }
    
    // Supprimer une tranche de paiement
    const removeTranche = (index) => {
      if (paymentData.value.tranches.length > 1) {
        paymentData.value.tranches.splice(index, 1)
      }
    }
    
    // Mettre à jour le montant total (pour compatibilité)
    const updateTotalMontant = () => {
      paymentData.value.montant = totalTranches.value
    }
    
    // Vérifier si un produit est sélectionné
    const isProductSelected = (productId) => {
      return selectedProducts.value.some(p => p._id === productId)
    }
    
    // Toggle la sélection d'un produit
    const toggleProductSelection = (product) => {
      const index = selectedProducts.value.findIndex(p => p._id === product._id)
      if (index === -1) {
        // Ajouter le produit
        selectedProducts.value.push(product)
        selectedProduct.value = product // Pour compatibilité
        toast.success(`✅ ${product.designation} ajouté`)
      } else {
        // Retirer le produit
        selectedProducts.value.splice(index, 1)
        if (selectedProducts.value.length > 0) {
          selectedProduct.value = selectedProducts.value[selectedProducts.value.length - 1]
        } else {
          selectedProduct.value = null
        }
        toast.info(`❌ ${product.designation} retiré`)
      }
    }
    
    // Retirer un produit de la sélection
    const removeSelectedProduct = (productId) => {
      const index = selectedProducts.value.findIndex(p => p._id === productId)
      if (index !== -1) {
        const product = selectedProducts.value[index]
        selectedProducts.value.splice(index, 1)
        if (selectedProducts.value.length > 0) {
          selectedProduct.value = selectedProducts.value[selectedProducts.value.length - 1]
        } else {
          selectedProduct.value = null
        }
        toast.info(`❌ ${product.designation} retiré`)
      }
    }
    
    // Méthodes
    const closeModal = () => {
      emit('close')
      resetModal()
    }
    
    const resetModal = () => {
      currentStep.value = 1
      selectedProduct.value = null
      selectedProducts.value = []
      quantity.value = 1
      searchQuery.value = ''
      materialData.value = { imeiIccid: '', numeroSerie: '' }
      paymentData.value = { paiementCollecte: false, montant: 0, modePaiement: '', justificatifs: [], justificationMontant: '', tranches: [{ montant: 0, modePaiement: '' }], codeLog: '' }
      finalizationData.value = { commentaires: '' }
    }
    
    const nextStep = () => {
      if (canProceedToNextStep.value && currentStep.value < totalSteps.value) {
        currentStep.value++
      }
    }
    
    const previousStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--
      }
    }
    
    const selectProduct = (product) => {
      selectedProduct.value = product
      quantity.value = 1
      
      console.log('🔍 Produit sélectionné:', product)
      
      // Réinitialiser les champs matériel
      materialData.value.imeiIccid = ''
      materialData.value.numeroSerie = ''
      
      let autoFilledFields = []
      
      // Auto-remplissage des données matériel depuis le produit sélectionné
      if (product.imei) {
        materialData.value.imeiIccid = product.imei
        autoFilledFields.push(`IMEI: ${product.imei}`)
      } else if (product.iccid) {
        materialData.value.imeiIccid = product.iccid
        autoFilledFields.push(`ICCID: ${product.iccid}`)
      }
      
      if (product.numeroSerie) {
        materialData.value.numeroSerie = product.numeroSerie
        autoFilledFields.push(`Série: ${product.numeroSerie}`)
      }
      
      // Notification de succès si des champs ont été remplis
      if (autoFilledFields.length > 0) {
        toast.success(`✨ Auto-remplissage: ${autoFilledFields.join(', ')}`)
      } else {
        toast.info('ℹ️ Aucune donnée matériel disponible pour ce produit')
      }
      
      console.log('🔍 Auto-remplissage matériel:', {
        produit: product.designation,
        categorie: product.categorie,
        imei: product.imei,
        iccid: product.iccid,
        numeroSerie: product.numeroSerie,
        champsRemplis: autoFilledFields,
        resultat: materialData.value
      })
    }
    
    const incrementQuantity = () => {
      const maxQuantity = selectedProduct.value.stockCount || selectedProduct.value.quantite || 1
      if (quantity.value < maxQuantity) {
        quantity.value++
      }
    }
    
    const decrementQuantity = () => {
      if (quantity.value > 1) {
        quantity.value--
      }
    }
    
    const formatPrice = (price) => {
      return new Intl.NumberFormat('fr-TN', {
        style: 'currency',
        currency: 'TND'
      }).format(price || 0)
    }
    
    // Fonction pour afficher l'identifiant du produit selon sa catégorie
    // GPS → IMEI, SIM → ICCID, autres → referenceInterne
    const getProductIdentifier = (product) => {
      if (!product) return ''
      const categorie = (product.categorie || '').toUpperCase()
      
      if (categorie === 'GPS' && product.imei) {
        return `IMEI: ${product.imei}`
      }
      if (categorie === 'SIM' && product.iccid) {
        return `ICCID: ${product.iccid}`
      }
      // Fallback sur referenceInterne
      return product.referenceInterne || ''
    }
    
    const loadProducts = async () => {
      try {
        const token = localStorage.getItem('token')
        
        // Utiliser la même logique que le modal "Mon Sac" : store Vuex
        const userRole = store.getters['auth/userRole']
        const userId = store.getters['auth/user']?._id
        
        console.log('🔍 Debug - userRole from store:', userRole)
        console.log('🔍 Debug - userId from store:', userId)
        console.log('🔍 Debug - user object from store:', store.getters['auth/user'])
        
        let technicienId = null
        
        // Si l'utilisateur est un technicien, utiliser son ID
        if (userRole === 'Technicien' && userId) {
          technicienId = userId
          console.log('🔍 Utilisation de l\'ID du technicien connecté (store Vuex):', technicienId)
        } 
        // Sinon (Admin ou autre), utiliser le technicien de l'intervention
        else if (props.intervention?.technicien) {
          technicienId = props.intervention.technicien._id || props.intervention.technicien
          console.log('🔍 Utilisation de l\'ID du technicien de l\'intervention:', technicienId)
        } else {
          console.warn('⚠️ Impossible de déterminer le technicien:', { userRole, userId, intervention: props.intervention })
        }
        
        console.log('🔍 Debug - Intervention:', props.intervention)
        console.log('🔍 Debug - Technicien ID final:', technicienId)
        
        if (!technicienId) {
          // Ne pas afficher d'erreur, simplement initialiser avec une liste vide
          console.log('ℹ️ Pas de technicien assigné, liste de produits vide')
          products.value = []
          return
        }
        
        // Utiliser l'API spécifique pour les produits du technicien
        const apiUrl = `/api/stock/technicien/${technicienId}/produits`
        console.log('🔍 Debug - URL API appelée:', apiUrl)
        console.log('🔍 Debug - Token présent:', !!token)
        
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        console.log('🔍 Debug - Statut réponse:', response.status)
        console.log('🔍 Debug - Response OK:', response.ok)
        
        if (response.ok) {
          const data = await response.json()
          console.log('🔍 Debug - Réponse API technicien:', data)
          
          // L'API retourne { success: true, data: { products } }
          const productsList = data.data?.products || []
          console.log('🔍 Debug - Liste produits du sac technicien:', productsList)
          
          if (Array.isArray(productsList)) {
            // L'API filtre déjà par statut 'affecte_technicien', pas besoin de re-filtrer
            products.value = productsList
            console.log('🔍 Debug - Produits dans le sac:', products.value)
            
            if (products.value.length === 0) {
              toast.warning('⚠️ Aucun produit disponible dans le sac du technicien')
            }
            // Notification supprimée pour éviter le spam
          } else {
            console.error('❌ La liste des produits n\'est pas un tableau:', typeof productsList)
            toast.error('Format de données incorrect reçu du serveur')
            products.value = []
          }
        } else {
          const errorText = await response.text().catch(() => 'Erreur inconnue')
          console.error('❌ Erreur HTTP:', response.status, response.statusText)
          console.error('❌ Détails erreur:', errorText)
          toast.error(`Erreur serveur: ${response.status} - ${errorText}`)
          products.value = []
        }
      } catch (error) {
        console.error('❌ Erreur chargement produits du technicien:', error)
        toast.error('Erreur lors du chargement des produits du technicien')
        products.value = []
      }
    }
    
    // Fonction pour générer et télécharger le reçu automatiquement
    const generateReceipt = async () => {
      try {
        const intervention = props.intervention
        console.log('🔍 Debug - Génération reçu pour intervention:', intervention)
        
        toast.info('📄 Génération du reçu en cours...')
        
        const recuId = intervention._id?.substring(intervention._id.length - 8).toUpperCase() || 'XXXXXXXX'
        
        // Générer le QR Code avec l'URL du reçu (utiliser le domaine de production)
        const qrCodeUrl = `${RECU_BASE_URL}/recu/${intervention._id}`
        let qrCodeDataUrl = null
        try {
          qrCodeDataUrl = await QRCode.toDataURL(qrCodeUrl, {
            width: 100,
            margin: 1,
            color: {
              dark: '#000000',
              light: '#ffffff'
            },
            errorCorrectionLevel: 'M'
          })
        } catch (qrError) {
          console.warn('⚠️ Erreur génération QR Code:', qrError)
        }
        
        // Calculer la hauteur exacte du contenu
        let contentHeight = 10 // Marge initiale y=10
        contentHeight += 5 + 4 + 4 + 4 + 6 // Header AMANA (5+4+4+4+6=23)
        contentHeight += 6 // Date
        contentHeight += 6 // Ligne séparation + espace
        contentHeight += 6 + 6 // Titre REÇU + Numéro
        contentHeight += 6 // Ligne séparation
        contentHeight += 4 // Demandeur
        
        // Adresse client pour calcul hauteur
        let hasClientAddress = false
        if (intervention.prospect?.client?.adresse) {
          const addr = intervention.prospect.client.adresse
          if (addr.rue || addr.ville || addr.codePostal || addr.region) {
            hasClientAddress = true
          }
        }
        if (hasClientAddress) contentHeight += 4
        
        contentHeight += 6 // Ligne séparation
        
        // Appareils
        let appareilsCount = 0
        if (intervention.prospect?.categories?.length > 0) {
          appareilsCount = intervention.prospect.categories.length
        } else if (intervention.prospect?.appareils?.length > 0) {
          appareilsCount = intervention.prospect.appareils.length
        } else {
          appareilsCount = 1
        }
        contentHeight += appareilsCount * 4 + 2 // Appareils
        
        // Calculer la hauteur pour les tranches de paiement
        const tranchesForHeight = paymentData.value.tranches.filter(t => t.montant > 0 && t.modePaiement)
        const tranchesHeight = tranchesForHeight.length > 1 ? (tranchesForHeight.length * 4 + 6) : 4
        contentHeight += 4 + tranchesHeight + 6 // Technicien + Paiement (tranches) + espace
        contentHeight += 1 + 1 + 6 // Double ligne + espace
        contentHeight += 6 // Total
        contentHeight += 1 + 1 + 8 // Double ligne + espace
        contentHeight += 4 + 4 + 8 // Footer texte
        contentHeight += 5 // Barcode
        contentHeight += 5 // Numéro sous barcode
        contentHeight += 8 // Espace avant QR code
        contentHeight += 25 + 3 + 5 // QR code (25) + espace (3) + texte (5)
        
        const finalHeight = contentHeight + 5 // Marge finale
        
        const doc = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: [80, finalHeight]
        })
        
        const pageWidth = 80
        const centerX = pageWidth / 2
        let y = 10

        // ========== LOGO AMANA EN HAUT À DROITE ==========
        const logoSize = 18
        const logoX = pageWidth - logoSize - 5
        const logoY = 5
        try {
          const logoImg = new Image()
          logoImg.src = require('@/assets/amana1.jpeg')
          doc.addImage(logoImg, 'JPEG', logoX, logoY, logoSize, logoSize)
        } catch (e) {
          console.log('Logo non chargé:', e)
        }

        // ========== HEADER AMANA ==========
        doc.setFontSize(14)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(0, 0, 0)
        doc.text('AMANA', 8, y) // Aligné à gauche pour laisser place au QR
        y += 5
        
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
     
        y += 4
        doc.text('9 AV Bardo Hbib,Bourgiba Tunis', 8, y)
        y += 4
        doc.text('Tel: +216 96 677 399', 8, y)
        y += 6

        // Date
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.text(`Date: ${new Date().toLocaleDateString('fr-FR')}`, 8, y)
        y += 6

        // Ligne de séparation
        doc.setLineWidth(0.3)
        doc.line(8, y, pageWidth - 8, y)
        y += 6

        // ========== TITRE ==========
        doc.setFontSize(11)
        doc.setFont('helvetica', 'bold')
        doc.text('REÇU DE CAISSE', centerX, y, { align: 'center' })
        y += 6

        // Numéro en bas du titre
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.text(`N° ${recuId}`, centerX, y, { align: 'center' })
        y += 6

        // Ligne de séparation
        doc.setLineWidth(0.3)
        doc.line(8, y, pageWidth - 8, y)
        y += 6

        // Demandeur (Client)
        let clientFullName = 'Non renseigné'
        let clientAddress = ''
        let clientTelephone = ''
        let clientCodeLog = ''
        if (intervention.prospect?.client) {
          const client = intervention.prospect.client
          if (client.nomComplet) {
            clientFullName = client.nomComplet
          } else if (client.nom || client.prenom) {
            clientFullName = `${client.nom || ''} ${client.prenom || ''}`.trim()
          }
          
          // Récupérer le téléphone
          if (client.telephone) {
            clientTelephone = client.telephone
          }
          
          // Récupérer le code log (du client, de l'intervention ou du paymentData)
          clientCodeLog = client.codeLog || intervention.codeLog || paymentData.value.codeLog || ''
          
          // Formater l'adresse
          if (client.adresse) {
            const addr = client.adresse
            const parts = []
            if (addr.rue) parts.push(addr.rue)
            if (addr.ville) parts.push(addr.ville)
            if (addr.codePostal) parts.push(addr.codePostal)
            if (addr.region) parts.push(addr.region)
            clientAddress = parts.join(', ')
          }
        }
        
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.text(`Client: ${clientFullName}`, 8, y)
        y += 4
        if (clientTelephone) {
          doc.text(`Tél: ${clientTelephone}`, 8, y)
          y += 4
        }
         if (clientAddress) {
          doc.text(`Adresse: ${clientAddress}`, 8, y)
          y += 4
        }
        if (clientCodeLog) {
          doc.text(`Login: ${clientCodeLog}`, 8, y)
          y += 4
        }
       

        // Ligne de séparation
        doc.setLineWidth(0.3)
        doc.line(8, y, pageWidth - 8, y)
        y += 6

        // Type d'intervention avec appareils
        const typeIntervention = intervention.prospect?.typeIntervention || 'Installation'

        // Récupérer les appareils
        let appareilsList = []
        if (intervention.prospect?.categories && intervention.prospect.categories.length > 0) {
          intervention.prospect.categories.forEach(cat => {
            if (cat.nom && cat.quantite) {
              appareilsList.push({
                nom: cat.nom,
                quantite: cat.quantite
              })
            } else if (cat.categorieId?.nom && cat.quantite) {
              appareilsList.push({
                nom: cat.categorieId.nom,
                quantite: cat.quantite
              })
            }
          })
        } else if (intervention.prospect?.appareils && intervention.prospect.appareils.length > 0) {
          intervention.prospect.appareils.forEach(app => {
            if (app.type && app.quantite) {
              appareilsList.push({
                nom: app.type,
                quantite: app.quantite
              })
            }
          })
        } else if (intervention.prospect?.typeAppareil) {
          appareilsList.push({
            nom: intervention.prospect.typeAppareil,
            quantite: intervention.prospect.nombreAppareils || 1
          })
        }

        // Afficher Type d'intervention avec appareils
        if (appareilsList.length > 0) {
          appareilsList.forEach(appareil => {
            doc.setFontSize(8)
            doc.setFont('helvetica', 'bold')
            doc.text(`${typeIntervention}: ${appareil.nom} (${appareil.quantite})`, 8, y)
            y += 4
          })
        } else {
          doc.setFontSize(8)
          doc.setFont('helvetica', 'bold')
          doc.text(`${typeIntervention}: Service`, 8, y)
          y += 4
        }
        y += 2

        // Fournisseur (Technicien)
        let technicienFullName = 'Non assigné'
        if (intervention.technicien) {
          technicienFullName = `${intervention.technicien.prenom || ''} ${intervention.technicien.nom || ''}`.trim() || 'Non assigné'
        }
        
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.text(`Technicien: ${technicienFullName}`, 8, y)
        y += 4
          
        // Mode de paiement - Afficher toutes les tranches
        const modePaiementLabels = {
          'especes': 'Espèces',
          'Espèces': 'Espèces',
          'cheque': 'Chèque',
          'Chèque': 'Chèque',
          'carte_bancaire': 'Carte bancaire',
          'CB': 'Carte bancaire',
          'virement': 'Virement',
          'Virement': 'Virement',
          'prelevement': 'Prélèvement',
          'Prélèvement': 'Prélèvement'
        }
        
        // Afficher les tranches de paiement
        const tranchesValides = paymentData.value.tranches.filter(t => t.montant > 0 && t.modePaiement)
        if (tranchesValides.length > 1) {
          doc.setFont('helvetica', 'bold')
          doc.text('Paiement:', 8, y)
          y += 4
          doc.setFont('helvetica', 'normal')
          tranchesValides.forEach((tranche, index) => {
            const modeLabel = modePaiementLabels[tranche.modePaiement] || tranche.modePaiement
            doc.text(`  • ${modeLabel}: ${formatPrice(tranche.montant)}`, 8, y)
            y += 4
          })
          y += 2
        } else {
          const modePaiement = tranchesValides.length > 0 ? tranchesValides[0].modePaiement : 'Espèces'
          doc.text(`Paiement: ${modePaiementLabels[modePaiement] || modePaiement}`, 8, y)
          y += 6
        }

        // Ligne de séparation double
        doc.setLineWidth(0.5)
        doc.line(8, y, pageWidth - 8, y)
        y += 1
        doc.line(8, y, pageWidth - 8, y)
        y += 6

        // TOTAL - utiliser le montant du paiement collecté ou le prix du RDV
        const montantTotal = paymentData.value.paiementCollecte 
          ? paymentData.value.montant 
          : (intervention.prospect?.prix || 0)
        const montantFormate = new Intl.NumberFormat('fr-FR', { 
          minimumFractionDigits: 0,
          maximumFractionDigits: 0 
        }).format(montantTotal)
        
        doc.setFontSize(10)
        doc.setFont('helvetica', 'bold')
        doc.text('TOTAL:', 8, y)
        doc.text(`${montantFormate} TND`, pageWidth - 8, y, { align: 'right' })
        y += 6

        // Ligne de séparation double
        doc.setLineWidth(0.5)
        doc.line(8, y, pageWidth - 8, y)
        y += 1
        doc.line(8, y, pageWidth - 8, y)
        y += 8

        // Footer
        doc.setFontSize(7)
        doc.setFont('helvetica', 'italic')
        doc.setTextColor(100, 100, 100)
        doc.text('Merci de votre confiance', centerX, y, { align: 'center' })
        y += 4
        doc.text('Amana Management Suite', centerX, y, { align: 'center' })
        y += 8

        // Barcode simulé
        doc.setFontSize(20)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(0, 0, 0)
        const barcodeText = '| || ||| || | || || | ||| || ||| ||| ||'
        doc.text(barcodeText, centerX, y, { align: 'center' })
        y += 5
        doc.setFontSize(8)
        doc.text(recuId, centerX, y, { align: 'center' })
        y += 8
        
        // QR Code en bas du reçu
        if (qrCodeDataUrl) {
          const qrSize = 25
          const qrX = (pageWidth - qrSize) / 2
          doc.addImage(qrCodeDataUrl, 'PNG', qrX, y, qrSize, qrSize)
          y += qrSize + 3
          doc.setFontSize(6)
          doc.setTextColor(100, 100, 100)
          doc.text('Scannez pour télécharger le reçu', centerX, y, { align: 'center' })
        }

        // Télécharger le PDF avec Capacitor Filesystem (compatible mobile)
        const fileName = `recu_caisse_${recuId}.pdf`
        const result = await downloadPDFMobile(doc, fileName)
        
        if (result.success) {
          toast.success(`✅ ${result.message}`)
        } else {
          toast.warning('⚠️ Intervention terminée mais erreur lors du téléchargement du reçu')
        }
      } catch (error) {
        console.error('❌ Erreur génération reçu:', error)
        toast.warning('⚠️ Intervention terminée mais erreur lors de la génération du reçu')
      }
    }
    
    const completeIntervention = async () => {
      isProcessing.value = true
      
      try {
        // Finaliser l'intervention directement (le backend accepte tous les statuts)
        // Le produit passe de 'affecte_technicien' à 'installe' lors de la clôture
        console.log('🔄 Clôture de l\'intervention...', 'Statut actuel:', props.intervention.statut, 'Type:', props.intervention.prospect?.typeIntervention)
        await finalizeIntervention()
        
        // Créer une créance si le montant payé est inférieur au prix de la mission
        if (paymentData.value.paiementCollecte && totalTranches.value < prixIntervention.value) {
          await createCreance()
        }
        
        // Pour les interventions normales (pas recouvrement)
        if (!isRecouvrement.value) {
          // Retirer les produits affectés de la liste locale du sac
          if (selectedProducts.value.length > 0) {
            const selectedIds = selectedProducts.value.map(p => p._id)
            products.value = products.value.filter(p => !selectedIds.includes(p._id))
          }
        }
        
        // Générer et télécharger automatiquement le reçu pour tous les types
        // (Installation, Intervention, Recouvrement)
        await generateReceipt()
        
        const successMessage = isRecouvrement.value 
          ? '✅ Recouvrement validé avec succès !' 
          : '✅ Intervention terminée avec succès !'
        
        toast.success(successMessage)
        emit('intervention-completed')
        emit('refresh-bag') // Émettre l'événement pour rafraîchir le sac technicien
        closeModal()
      } catch (error) {
        console.error('❌ Erreur finalisation:', error)
        toast.error(`Erreur lors de la finalisation: ${error.message}`)
      } finally {
        isProcessing.value = false
      }
    }
    
    // Créer une créance si paiement partiel
    const createCreance = async () => {
      try {
        const token = localStorage.getItem('token')
        const montantCreance = prixIntervention.value - totalTranches.value
        
        console.log('💰 Création créance:', {
          prixMission: prixIntervention.value,
          montantPaye: totalTranches.value,
          montantCreance
        })
        
        const response = await fetch('/api/creances', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            rdvId: props.intervention._id,
            prixMission: prixIntervention.value,
            montantPaye: totalTranches.value,
            clientId: props.intervention.prospect?.client?._id,
            prospectId: props.intervention.prospect?._id
          })
        })
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          console.warn('⚠️ Erreur création créance:', errorData.message)
          // Ne pas bloquer la clôture si la créance échoue
          return
        }
        
        const result = await response.json()
        console.log('✅ Créance créée:', result)
        toast.info(`💰 Créance de ${montantCreance} TND créée pour validation`)
        
      } catch (error) {
        console.warn('⚠️ Erreur création créance:', error)
        // Ne pas bloquer la clôture
      }
    }
    
    const startIntervention = async () => {
      const token = localStorage.getItem('token')
      
      console.log('🔍 Debug - Démarrage intervention ID:', props.intervention._id)
      
      const response = await fetch(`/api/rdv/${props.intervention._id}/demarrer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          latitude: null,  // Optionnel selon l'API
          longitude: null  // Optionnel selon l'API
        })
      })
      
      console.log('🔍 Debug - Statut réponse démarrage:', response.status)
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Erreur inconnue')
        let errorData = {}
        try {
          errorData = JSON.parse(errorText)
        } catch (e) {
          errorData = { message: errorText }
        }
        
        console.error('❌ Erreur API démarrage:', response.status, errorData)
        
        // Si l'intervention est déjà démarrée, ne pas considérer comme une erreur
        if (errorData.message && errorData.message.includes('déjà')) {
          console.log('ℹ️ Intervention déjà démarrée, on continue...')
          return
        }
        
        throw new Error(errorData.message || `Erreur démarrage (${response.status})`)
      }
      
      const result = await response.json()
      console.log('✅ Intervention démarrée:', result)
    }
    
    const affectProduct = async () => {
      const token = localStorage.getItem('token')
      
      const movementData = {
        produit: selectedProduct.value._id,
        typeMouvement: 'sortie_intervention',
        quantite: -quantity.value,
        emplacementOrigine: selectedProduct.value.emplacement,
        emplacementDestination: 'intervention',
        motif: `Affectation intervention ${props.intervention._id}`,
        intervention: props.intervention._id,
        technicien: props.intervention.technicien?._id || store.getters['auth/user']?._id,
        client: props.intervention.prospect?.client?.nom || props.intervention.prospect?.nom
      }
      
      console.log('🔍 Debug - Mouvement stock:', movementData)
      
      const response = await fetch('/api/movements', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(movementData)
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('❌ Erreur API mouvement:', errorData)
        throw new Error(errorData.message || 'Erreur affectation produit')
      }
      
      const result = await response.json()
      console.log('✅ Mouvement créé:', result)
    }
    
    const finalizeIntervention = async () => {
      const token = localStorage.getItem('token')
      
      // Initialiser le payload de base
      const finalizationPayload = {
        commentaires: finalizationData.value.commentaires || '',
        paiementCollecte: paymentData.value.paiementCollecte,
        // Code Log - utiliser l'existant ou le nouveau
        codeLog: hasExistingCodeLog.value ? existingCodeLog.value : paymentData.value.codeLog
      }
      
      // Pour le recouvrement, ne pas inclure checklist, IMEI et photos
      if (!isRecouvrement.value) {
        // Préparer les données IMEI/ICCID pour tous les produits sélectionnés
        const imeiIccidScanne = []
        selectedProducts.value.forEach(product => {
          const imeiIccidValue = product.imei || product.iccid || ''
          if (imeiIccidValue) {
            imeiIccidScanne.push({
              type: imeiIccidValue.length === 15 ? 'IMEI' : 'ICCID',
              valeur: imeiIccidValue,
              dateScanne: new Date().toISOString(),
              produit: product._id,
              designation: product.designation
            })
          }
        })
        
        // Pour compatibilité, utiliser le premier IMEI/ICCID
        const imeiIccidValue = imeiIccidScanne.length > 0 ? imeiIccidScanne[0].valeur : ''
        
        // Formater les photos avec le bon format pour le backend
        const photosFormatted = (appareilPhotos.value || []).map(photoData => ({
          data: photoData,
          type: 'apres', // Type 'apres' pour les photos de l'appareil installé
          description: 'Photo appareil installé',
          timestamp: new Date().toISOString()
        }))
        
        finalizationPayload.checklist = {
          materielInstalle: true,
          testConnexion: true,
          formationClient: true,
          documentationRemise: true
        }
        // Ajouter checklistValidation pour l'affichage dans les détails
        finalizationPayload.checklistValidation = [
          { id: 'materielInstalle', label: 'Matériel installé', completed: true },
          { id: 'testConnexion', label: 'Test de connexion', completed: true },
          { id: 'formationClient', label: 'Formation client', completed: true },
          { id: 'documentationRemise', label: 'Documentation remise', completed: true }
        ]
        finalizationPayload.imeiIccid = imeiIccidValue // Format simple pour compatibilité
        finalizationPayload.imeiIccidScanne = imeiIccidScanne // Format tableau pour l'API
        finalizationPayload.photos = photosFormatted // Photos formatées pour le backend
        finalizationPayload.photosAppareil = photosFormatted // Alias pour compatibilité
      } else {
        // Pour le recouvrement, ajouter une checklist vide pour éviter les erreurs
        finalizationPayload.checklist = { recouvrement: true }
        finalizationPayload.imeiIccid = 'RECOUVREMENT'
      }
      
      if (paymentData.value.paiementCollecte) {
        // Préparer les tranches de paiement
        const tranchesValides = paymentData.value.tranches.filter(t => t.montant > 0 && t.modePaiement)
        const montantTotal = tranchesValides.reduce((sum, t) => sum + parseFloat(t.montant), 0)
        
        // Mode de paiement principal (pour compatibilité) - prendre le premier ou "Multiple" si plusieurs
        const modePaiementPrincipal = tranchesValides.length === 1 
          ? tranchesValides[0].modePaiement 
          : 'Multiple'
        
        finalizationPayload.encaissement = {
          montant: montantTotal,
          modePaiement: modePaiementPrincipal,
          tranches: tranchesValides.map(t => ({
            montant: parseFloat(t.montant),
            modePaiement: t.modePaiement
          })),
          dateEncaissement: new Date().toISOString(),
          justificatifs: paymentData.value.justificatifs || [],
          justificationMontant: paymentData.value.justificationMontant || ''
        }
      }
      
      console.log('🔍 Debug - Payload finalisation:', finalizationPayload)
      console.log('🔍 Debug - Intervention ID:', props.intervention._id)
      console.log('🔍 Debug - Token présent:', !!token)
      
      const response = await fetch(`/api/rdv/${props.intervention._id}/cloturer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(finalizationPayload)
      })
      
      console.log('🔍 Debug - Statut réponse clôture:', response.status)
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Erreur inconnue')
        let errorData = {}
        try {
          errorData = JSON.parse(errorText)
        } catch (e) {
          errorData = { message: errorText }
        }
        
        console.error('❌ Erreur API clôture:', response.status, errorData)
        throw new Error(errorData.message || `Erreur serveur (${response.status})`)
      }
      
      const result = await response.json()
      console.log('✅ Intervention Terminée:', result)
    }
    
    // Gestion des fichiers (justificatifs paiement)
    const fileInput = ref(null)
    
    const triggerFileUpload = () => {
      fileInput.value?.click()
    }
    
    const handleFileUpload = (event) => {
      const files = Array.from(event.target.files)
      
      files.forEach(file => {
        const reader = new FileReader()
        reader.onload = (e) => {
          paymentData.value.justificatifs.push(e.target.result)
        }
        reader.readAsDataURL(file)
      })
    }
    
    const removeFile = (index) => {
      paymentData.value.justificatifs.splice(index, 1)
    }
    
    // Gestion des photos de l'appareil
    const appareilPhotoInput = ref(null)
    
    const triggerAppareilPhotoUpload = () => {
      appareilPhotoInput.value?.click()
    }
    
    const handleAppareilPhotoUpload = (event) => {
      const files = Array.from(event.target.files)
      
      files.forEach(file => {
        const reader = new FileReader()
        reader.onload = (e) => {
          appareilPhotos.value.push(e.target.result)
          toast.success('📷 Photo ajoutée')
        }
        reader.readAsDataURL(file)
      })
    }
    
    const removeAppareilPhoto = (index) => {
      appareilPhotos.value.splice(index, 1)
      toast.info('🗑️ Photo supprimée')
    }
    
    onMounted(() => {
      loadProducts()
    })
    
    return {
      // État
      currentStep,
      isProcessing,
      steps,
      isRecouvrement,
      totalSteps,
      realStep,
      
      // Données
      selectedProduct,
      selectedProducts,
      quantity,
      searchQuery,
      filteredProducts,
      materialData,
      paymentData,
      finalizationData,
      appareilPhotos,
      
      // Computed
      canProceedToNextStep,
      totalSelectedPrice,
      prixIntervention,
      totalTranches,
      existingCodeLog,
      hasExistingCodeLog,
      isInstallation,
      isCodeLogRequired,
      
      // Méthodes
      closeModal,
      nextStep,
      previousStep,
      selectProduct,
      toggleProductSelection,
      isProductSelected,
      removeSelectedProduct,
      incrementQuantity,
      decrementQuantity,
      formatPrice,
      getProductIdentifier,
      completeIntervention,
      addTranche,
      removeTranche,
      updateTotalMontant,
      
      // Fichiers justificatifs paiement
      fileInput,
      triggerFileUpload,
      handleFileUpload,
      removeFile,
      
      // Photos appareil
      appareilPhotoInput,
      triggerAppareilPhotoUpload,
      handleAppareilPhotoUpload,
      removeAppareilPhoto
    }
  }
}
</script>

<style scoped>
/* Variables CSS */
:root {
  --primary-blue: #001BB7;
  --secondary-blue: #0046FF;
}

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.modal-container {
  background: white;
  border-radius: 20px;
  width: 95%;
  max-width: 1200px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
  animation: modalSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  display: flex;
  flex-direction: column;
}

/* Header */
.modal-header {
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
  position: relative;
  overflow: hidden;
}

.modal-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
  backdrop-filter: blur(10px);
}

.header-content {
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
}

.header-info h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
}

.header-info p {
  margin: 0.25rem 0 0 0;
  opacity: 0.9;
  font-size: 0.9rem;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

/* Stepper */
.stepper-container {
  position: relative;
  z-index: 2;
  padding: 0 2rem 1.5rem 2rem;
}

.stepper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}

.step-item {
  display: flex;
  align-items: center;
  flex: 1;
  position: relative;
}

.step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  transition: all 0.3s ease;
  z-index: 2;
  position: relative;
}

.step-item.active .step-circle {
  background: white;
  color: var(--primary-blue);
  border-color: white;
  box-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
}

.step-item.completed .step-circle {
  background: #28a745;
  border-color: #28a745;
  color: white;
}

.step-content {
  margin-left: 0.75rem;
  flex: 1;
}

.step-title {
  font-weight: 600;
  font-size: 0.9rem;
}

.step-subtitle {
  font-size: 0.75rem;
  opacity: 0.8;
  margin-top: 0.25rem;
}

.step-connector {
  position: absolute;
  top: 50%;
  left: 40px;
  right: -40px;
  height: 2px;
  background: rgba(255, 255, 255, 0.3);
  z-index: 1;
}

.step-item.completed .step-connector {
  background: #28a745;
}

/* Barre de prix RDV */
.prix-rdv-bar {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
  padding: 0.75rem 2rem;
}

.prix-rdv-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 100%;
}

.prix-rdv-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #495057;
  font-weight: 500;
}

.prix-rdv-label i {
  color: var(--primary-blue);
  font-size: 1.1rem;
}

.prix-rdv-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #28a745;
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 1px solid #28a745;
}

/* Modal Body */
.modal-body {
  padding: 2rem;
  max-height: 60vh;
  overflow-y: auto;
}

.step-content-wrapper {
  animation: stepSlideIn 0.4s ease;
}

.step-header {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.05) 0%, rgba(0, 70, 255, 0.05) 100%);
  border-radius: 12px;
  border-left: 4px solid var(--primary-blue);
}

.step-icon {
  font-size: 2rem;
  color: var(--primary-blue);
  margin-right: 1rem;
}

.step-header h3 {
  margin: 0;
  color: var(--primary-blue);
  font-size: 1.25rem;
}

.step-header p {
  margin: 0.25rem 0 0 0;
  color: #666;
  font-size: 0.9rem;
}

/* Étape 1: Affectation Produit */
.search-section {
  margin-bottom: 1.5rem;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-input-wrapper i {
  position: absolute;
  left: 1rem;
  color: #666;
  z-index: 2;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(0, 27, 183, 0.1);
}

.products-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 0.5rem;
}

.no-products-message {
  text-align: center;
  padding: 2rem;
  color: #666;
}

.no-products-message i {
  font-size: 2rem;
  color: #ffc107;
  margin-bottom: 1rem;
}

.no-products-message p {
  margin: 0.5rem 0;
  font-weight: 600;
  color: #333;
}

.no-products-message small {
  color: #666;
  font-style: italic;
}

.product-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 0.5rem;
}

.product-card:hover {
  background: rgba(0, 27, 183, 0.05);
  transform: translateX(4px);
}

.product-card.selected {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.1) 0%, rgba(0, 70, 255, 0.1) 100%);
  border: 2px solid var(--primary-blue);
}

.product-info {
  flex: 1;
}

.product-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.product-ref {
  background: var(--primary-blue);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
}

.product-category {
  background: #f8f9fa;
  color: #666;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
}

.product-name {
  font-weight: 600;
  color: #333;
  margin-bottom: 0.25rem;
}

.product-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
  color: #666;
}

.product-price {
  font-weight: 600;
  color: var(--primary-blue);
}

.selection-indicator {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--primary-blue);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s ease;
}

.product-card.selected .selection-indicator {
  opacity: 1;
  animation: bounceIn 0.4s ease;
}

.quantity-section {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
}

.quantity-section label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
}

.quantity-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.quantity-controls button {
  width: 36px;
  height: 36px;
  border: 2px solid var(--primary-blue);
  background: white;
  color: var(--primary-blue);
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.quantity-controls button:hover:not(:disabled) {
  background: var(--primary-blue);
  color: white;
}

.quantity-controls button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quantity-controls input {
  width: 80px;
  padding: 0.5rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
}

.stock-info {
  font-size: 0.85rem;
  color: #666;
}

/* Étape 2: Matériel */
.material-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.auto-fill-notice {
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.1) 0%, rgba(32, 201, 151, 0.1) 100%);
  border: 1px solid rgba(40, 167, 69, 0.2);
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.auto-fill-notice i {
  color: #28a745;
  font-size: 1.2rem;
}

.auto-fill-content {
  flex: 1;
}

.auto-fill-title {
  color: #155724;
  font-size: 0.9rem;
  font-weight: 600;
  display: block;
  margin-bottom: 0.5rem;
}

.auto-fill-title strong {
  color: #28a745;
}

.auto-fill-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.auto-fill-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #28a745;
  font-size: 0.85rem;
  font-weight: 500;
}

.auto-fill-item i {
  width: 16px;
  text-align: center;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
}

.input-with-scanner {
  display: flex;
  gap: 0.5rem;
}

.material-input {
  flex: 1;
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.material-input:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(0, 27, 183, 0.1);
}

.material-input.auto-filled {
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.05) 0%, rgba(32, 201, 151, 0.05) 100%);
  border-color: rgba(40, 167, 69, 0.3);
  color: #28a745;
  font-weight: 600;
}

.material-input.auto-filled:focus {
  border-color: #28a745;
  box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
}

.scanner-btn {
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
}

.scanner-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(40, 167, 69, 0.3);
}

.input-help {
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: #666;
}

/* Étape 3: Paiement */
.payment-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Code Log Section */
.code-log-section {
  background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  border: 1px solid #fed7aa;
  border-radius: 12px;
  padding: 1rem;
}

.code-log-section label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #c2410c;
  margin-bottom: 0.5rem;
}

.code-log-section label i {
  color: #ea580c;
}

.code-log-section .form-control {
  border: 2px solid #fdba74;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 1rem;
}

.code-log-section .form-control:focus {
  border-color: #ea580c;
  box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
}

.code-log-section .form-hint {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: #9a3412;
}

.code-log-display {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1px solid #86efac;
  border-radius: 12px;
  padding: 1rem;
}

.code-log-display label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #166534;
  margin-bottom: 0.5rem;
}

.code-log-value {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #15803d;
}

.code-log-value i {
  color: #22c55e;
}

.payment-toggle {
  display: flex;
  align-items: center;
}

.toggle-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: 600;
  color: #333;
}

.toggle-input {
  display: none;
}

.toggle-slider {
  width: 50px;
  height: 26px;
  background: #ccc;
  border-radius: 13px;
  position: relative;
  margin-right: 0.75rem;
  transition: all 0.3s ease;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  top: 3px;
  left: 3px;
  transition: all 0.3s ease;
}

.toggle-input:checked + .toggle-slider {
  background: var(--primary-blue);
}

.toggle-input:checked + .toggle-slider::before {
  transform: translateX(24px);
}

.payment-details {
  padding: 1rem;
  background: rgba(0, 27, 183, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(0, 27, 183, 0.1);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.payment-input, .payment-select {
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.payment-input:focus, .payment-select:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(0, 27, 183, 0.1);
}

/* Styles pour montant différent du Prix RDV */
.payment-input.montant-different {
  border-width: 2px;
}

.payment-input.montant-superieur {
  border-color: #ffc107;
  background: rgba(255, 193, 7, 0.05);
}

.payment-input.montant-inferieur {
  border-color: #dc3545;
  background: rgba(220, 53, 69, 0.05);
}

.montant-difference-indicator {
  margin-top: 0.5rem;
}

.difference-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.difference-badge.superieur {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeeba 100%);
  color: #856404;
  border: 1px solid #ffc107;
}

.difference-badge.inferieur {
  background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
  color: #721c24;
  border: 1px solid #dc3545;
}

.difference-badge i {
  font-size: 0.75rem;
}

/* Section justification */
.justification-section {
  margin-top: 1rem;
  padding: 1rem;
  background: linear-gradient(135deg, #fff3cd 0%, #ffeeba 100%);
  border-radius: 12px;
  border: 1px solid #ffc107;
}

.justification-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #856404;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.justification-label i {
  color: #ffc107;
}

.justification-textarea {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #ffc107;
  border-radius: 8px;
  font-size: 0.9rem;
  resize: vertical;
  min-height: 80px;
  background: white;
  transition: all 0.3s ease;
}

.justification-textarea:focus {
  outline: none;
  border-color: #e0a800;
  box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.2);
}

.justification-textarea::placeholder {
  color: #999;
  font-style: italic;
}

.justification-hint {
  display: block;
  margin-top: 0.5rem;
  color: #856404;
  font-size: 0.8rem;
}

/* ========== STYLES TRANCHES DE PAIEMENT - DESIGN PROFESSIONNEL ========== */
.tranches-container {
  background: linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%);
  border-radius: 16px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.tranches-title-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid rgba(0, 27, 183, 0.1);
}

.tranches-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  font-size: 1rem;
  color: var(--primary-blue);
}

.tranches-title i {
  font-size: 1.1rem;
}

.btn-add-tranche {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.9rem;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
  border: none;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 27, 183, 0.25);
}

.btn-add-tranche:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 27, 183, 0.35);
}

.btn-add-tranche i {
  font-size: 0.9rem;
}

/* Liste des tranches */
.tranches-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.tranche-card {
  position: relative;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem 1rem 1rem 3rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
}

.tranche-card:hover {
  border-color: var(--primary-blue);
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.1);
}

.tranche-card.tranche-filled {
  border-color: #28a745;
  background: linear-gradient(135deg, #f0fff4 0%, #ffffff 100%);
}

.tranche-badge {
  position: absolute;
  left: -1px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
  box-shadow: 0 2px 8px rgba(0, 27, 183, 0.3);
}

.tranche-card.tranche-filled .tranche-badge {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
}

.tranche-fields {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 1rem;
  align-items: start;
}

.tranche-field label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 0.4rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.tranche-field label i {
  font-size: 0.7rem;
  color: var(--primary-blue);
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.tranche-input {
  width: 100%;
  padding: 0.6rem 3rem 0.6rem 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  transition: all 0.2s ease;
}

.tranche-input:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(0, 27, 183, 0.1);
}

.tranche-input::placeholder {
  color: #cbd5e1;
}

.input-suffix {
  position: absolute;
  right: 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #94a3b8;
}

/* Boutons de mode de paiement */
.mode-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.mode-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 70px;
}

.mode-btn i {
  font-size: 1rem;
  color: #64748b;
  transition: all 0.2s ease;
}

.mode-btn span {
  font-size: 0.7rem;
  font-weight: 600;
  color: #64748b;
  transition: all 0.2s ease;
}

.mode-btn:hover {
  border-color: #94a3b8;
  transform: translateY(-2px);
}

.mode-btn.active {
  border-color: var(--mode-color, var(--primary-blue));
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.05) 0%, rgba(0, 70, 255, 0.05) 100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.mode-btn.active i,
.mode-btn.active span {
  color: var(--mode-color, var(--primary-blue));
}

/* Bouton supprimer tranche */
.btn-delete-tranche {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 26px;
  height: 26px;
  background: #fee2e2;
  color: #dc2626;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: 0.7;
}

.btn-delete-tranche:hover {
  background: #dc2626;
  color: white;
  opacity: 1;
  transform: scale(1.1);
}

.btn-delete-tranche i {
  font-size: 0.75rem;
}

/* Carte récapitulatif */
.payment-summary-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 2px solid #e2e8f0;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  font-weight: 700;
  font-size: 0.85rem;
  color: #475569;
  border-bottom: 1px solid #e2e8f0;
}

.summary-header i {
  color: var(--primary-blue);
}

.summary-body {
  padding: 0.75rem 1rem;
}

.summary-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0;
}

.summary-label {
  font-size: 0.85rem;
  color: #64748b;
}

.summary-value {
  font-weight: 600;
  font-size: 0.9rem;
  color: #1e293b;
}

.summary-value.prix-rdv {
  color: var(--primary-blue);
}

.summary-line.total-line {
  border-top: 2px dashed #e2e8f0;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
}

.summary-value.total-value {
  font-size: 1.1rem;
  font-weight: 700;
}

.summary-value.status-ok {
  color: #16a34a;
}

.summary-value.status-warning {
  color: #d97706;
}

.summary-line.diff-line {
  padding-top: 0.25rem;
}

.summary-value.diff-value {
  font-size: 0.85rem;
}

.summary-value.diff-positive {
  color: #d97706;
}

.summary-value.diff-negative {
  color: #dc2626;
}

/* Indicateur de statut */
.summary-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  font-size: 0.8rem;
  font-weight: 600;
}

.summary-status.status-success {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  color: #166534;
}

.summary-status.status-warning {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
}

.summary-status i {
  font-size: 0.9rem;
}

.upload-zone {
  border: 2px dashed #ccc;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.upload-zone:hover {
  border-color: var(--primary-blue);
  background: rgba(0, 27, 183, 0.05);
}

.upload-zone i {
  font-size: 2rem;
  color: #ccc;
  margin-bottom: 0.5rem;
}

.uploaded-files {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  flex-wrap: wrap;
}

.file-item {
  position: relative;
}

.file-preview {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #e9ecef;
}

.remove-file {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 0.75rem;
}

/* Étape 3: Photo Appareil */
.photo-appareil-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.upload-zone-appareil {
  border: 3px dashed #28a745;
  border-radius: 16px;
  padding: 3rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.05) 0%, rgba(32, 201, 151, 0.05) 100%);
}

.upload-zone-appareil:hover {
  border-color: #20c997;
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.1) 0%, rgba(32, 201, 151, 0.1) 100%);
  transform: translateY(-2px);
}

.upload-zone-appareil .upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.upload-zone-appareil .upload-content i {
  color: #28a745;
  margin-bottom: 0.5rem;
}

.upload-zone-appareil .upload-content p {
  color: #333;
  font-weight: 600;
  margin: 0;
}

.upload-zone-appareil .upload-content small {
  color: #666;
  font-size: 0.8rem;
}

.appareil-photos-preview {
  padding: 1rem;
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.1) 0%, rgba(32, 201, 151, 0.1) 100%);
  border-radius: 12px;
  border: 2px solid rgba(40, 167, 69, 0.3);
}

.appareil-photos-preview label {
  display: block;
  font-weight: 600;
  color: #28a745;
  margin-bottom: 0.75rem;
}

.photos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 1rem;
}

.photo-item {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.photo-preview {
  width: 100%;
  height: 120px;
  object-fit: cover;
  display: block;
}

.remove-photo-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.4);
}

.remove-photo-btn:hover {
  background: #c82333;
  transform: scale(1.1);
}

.no-photo-message {
  text-align: center;
  padding: 2rem;
  color: #666;
  background: #f8f9fa;
  border-radius: 12px;
  border: 2px dashed #dee2e6;
}

.no-photo-message i {
  font-size: 2.5rem;
  color: #ffc107;
  margin-bottom: 1rem;
  display: block;
}

.no-photo-message p {
  margin: 0.5rem 0;
  font-weight: 600;
  color: #333;
}

.no-photo-message small {
  color: #666;
  font-style: italic;
}

/* Étape 4: Finalisation */
.finalization-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.comments-textarea {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  font-size: 0.9rem;
  resize: vertical;
  transition: all 0.3s ease;
}

.comments-textarea:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(0, 27, 183, 0.1);
}

.summary-section {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
  border-left: 4px solid var(--primary-blue);
}

.summary-section h4 {
  margin: 0 0 1rem 0;
  color: var(--primary-blue);
}

.summary-item {
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.summary-product-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: white;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
}

.summary-imei {
  color: #6c757d;
  font-size: 0.8rem;
}

.summary-price {
  margin-left: auto;
  font-weight: 600;
  color: var(--primary-blue);
}

.summary-total {
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 2px solid var(--primary-blue);
  font-size: 1rem;
}

/* Section produits sélectionnés */
.selected-products-section {
  margin-top: 1.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.1) 0%, rgba(32, 201, 151, 0.1) 100%);
  border-radius: 12px;
  border: 2px solid rgba(40, 167, 69, 0.3);
}

.selected-products-section label {
  display: block;
  font-weight: 600;
  color: #28a745;
  margin-bottom: 0.75rem;
}

.selected-products-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.selected-product-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.selected-product-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.selected-product-name {
  font-weight: 600;
  color: #333;
}

.selected-product-ref {
  font-size: 0.8rem;
  color: #6c757d;
}

.selected-product-price {
  font-weight: 600;
  color: var(--primary-blue);
}

.btn-remove-product {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-remove-product:hover {
  background: #c82333;
  transform: scale(1.1);
}

.selected-products-total {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 2px dashed rgba(40, 167, 69, 0.3);
  text-align: right;
  font-size: 1.1rem;
  color: #28a745;
}

/* Footer */
.modal-footer {
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  backdrop-filter: blur(10px);
  border-top: 1px solid #e9ecef;
}

.footer-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.btn-secondary, .btn-primary, .btn-success {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
  transform: translateY(-2px);
}

.btn-primary {
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 27, 183, 0.3);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-success {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
}

.btn-success:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(40, 167, 69, 0.3);
}

.btn-success:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes stepSlideIn {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes bounceIn {
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 0;
    align-items: flex-end;
  }
  
  .modal-container {
    width: 100%;
    max-width: 100%;
    max-height: 90vh;
    border-radius: 20px 20px 0 0;
    margin: 0;
    display: flex;
    flex-direction: column;
  }
  
  .modal-header {
    flex-shrink: 0;
    padding: 1rem;
  }
  
  .header-content {
    padding: 0.75rem;
  }
  
  .header-content h2 {
    font-size: 1.2rem;
  }
  
  .header-content p {
    font-size: 0.8rem;
  }
  
  .stepper-container {
    padding: 0.5rem;
    overflow-x: auto;
  }
  
  .stepper {
    flex-direction: row;
    gap: 0.5rem;
    min-width: max-content;
  }
  
  .step-item {
    flex-direction: column;
    align-items: center;
    min-width: 80px;
  }
  
  .step-circle {
    width: 35px;
    height: 35px;
    font-size: 0.9rem;
  }
  
  .step-content {
    text-align: center;
  }
  
  .step-title {
    font-size: 0.7rem;
  }
  
  .step-subtitle {
    font-size: 0.6rem;
    display: none;
  }
  
  .step-connector {
    display: none;
  }
  
  .prix-rdv-bar {
    padding: 0.5rem 1rem;
  }
  
  .prix-rdv-label {
    font-size: 0.85rem;
  }
  
  .prix-rdv-label i {
    font-size: 1rem;
  }
  
  .prix-rdv-value {
    font-size: 1rem;
    padding: 0.4rem 0.75rem;
  }
  
  .modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
    min-height: 0;
  }
  
  .step-header {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }
  
  .step-header h3 {
    font-size: 1rem;
  }
  
  .step-header p {
    font-size: 0.8rem;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .modal-footer {
    flex-shrink: 0;
    padding: 1rem;
    background: white;
    border-top: 1px solid #e9ecef;
    position: sticky;
    bottom: 0;
    z-index: 10;
  }
  
  .footer-actions {
    flex-direction: row;
    gap: 0.75rem;
    justify-content: space-between;
  }
  
  .btn-secondary, .btn-primary, .btn-success {
    flex: 1;
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
    justify-content: center;
  }
  
  .btn-secondary {
    flex: 0 0 auto;
    min-width: 100px;
  }
  
  .btn-primary, .btn-success {
    flex: 1;
  }
  
  /* Products list responsive */
  .products-list {
    max-height: 200px;
    overflow-y: auto;
  }
  
  .product-card {
    padding: 0.75rem;
  }
  
  .selected-products-section {
    padding: 0.75rem;
  }
  
  /* Étape 3 Paiement - Responsive */
  .tranches-container {
    padding: 0.75rem;
  }
  
  .tranches-title-bar {
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;
  }
  
  .btn-add-tranche {
    width: 100%;
    justify-content: center;
  }
  
  .tranche-card {
    padding: 0.75rem 0.75rem 0.75rem 2.5rem;
  }
  
  .tranche-badge {
    width: 24px;
    height: 24px;
    font-size: 0.75rem;
    left: 0.5rem;
  }
  
  .tranche-fields {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .montant-field {
    width: 100%;
  }
  
  .mode-field {
    width: 100%;
  }
  
  .mode-buttons {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }
  
  .mode-btn {
    flex-direction: row;
    min-width: unset;
    padding: 0.5rem;
    gap: 0.4rem;
  }
  
  .mode-btn i {
    font-size: 0.9rem;
  }
  
  .mode-btn span {
    font-size: 0.7rem;
  }
  
  .btn-delete-tranche {
    top: 0.25rem;
    right: 0.25rem;
    width: 22px;
    height: 22px;
  }
  
  .payment-summary-card {
    margin-top: 1rem;
  }
  
  .summary-body {
    padding: 0.75rem;
  }
  
  .summary-line {
    font-size: 0.85rem;
  }
  
  .justification-section {
    margin-top: 1rem;
  }
  
  .justification-textarea {
    font-size: 0.9rem;
  }
  
  .upload-zone {
    padding: 1rem;
  }
  
  .upload-zone i {
    font-size: 1.5rem;
  }
  
  .upload-zone p {
    font-size: 0.85rem;
  }
}
</style>
